import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  DialogService,
  MessageType,
} from '../../../../app/features/shared/services/dialog.service';
import { getLogger, markAllAsTouched } from '../../../../app/core/utils';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import invariant from 'tiny-invariant';
import { StorageProvider } from '../../storage.provider';
import { StorageItem } from '@uploadcam/common';

export interface RenameFolderDialogInput {
  folder: StorageItem;
}

export type RenameFolderDialogResult = StorageItem;

@Component({
  selector: 'app-rename-folder-dialog',
  templateUrl: './rename-folder-dialog.component.html',
  styleUrls: ['./rename-folder-dialog.component.scss'],
  standalone: false,
})
export class RenameFolderDialogComponent {
  public isRenaming = false;
  public readonly form = this.fb.group({
    name: [this.data.folder.name, [Validators.required]],
  });

  private readonly logger = getLogger('RenameFolderDialogComponent');

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: RenameFolderDialogInput,
    private readonly dialog: DialogService,
    private readonly dialogRef: MatDialogRef<RenameFolderDialogComponent, RenameFolderDialogResult>,
    private readonly fb: NonNullableFormBuilder,
    private readonly storageProvider: StorageProvider,
  ) {}

  public close(): void {
    this.dialogRef.close();
  }

  public async confirm(): Promise<void> {
    if (this.form.invalid) {
      return markAllAsTouched(this.form);
    }

    const name = this.form.value.name?.trim();

    // Nothing to do if the name hasn't changed.
    if (name === this.data.folder.name) {
      return this.dialogRef.close();
    }

    this.form.disable();
    this.isRenaming = true;
    this.dialogRef.disableClose = true;

    this.logger.info(`Renaming folder…`);

    try {
      invariant(name, 'Folder name must have a value');

      const manager = await this.storageProvider.provideManager();
      const folder = await manager.renameFolder(this.data.folder, name);

      this.dialog.snack(`Folder successfully renamed`, MessageType.Success);
      this.dialogRef.close(folder);
    } catch (err) {
      this.logger.error(err);
      this.dialog.showErrorAlert(err, 'error.fallback.folder.rename');
    } finally {
      this.form.enable();
      this.isRenaming = false;
      this.dialogRef.disableClose = false;
    }
  }
}
